import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepperCustomComponent } from '@shared/mat-stepper-custom/mat-stepper-custom.component';
import { ApiService } from '@services/api.service';
import { MatDialogCustomComponent } from '../../mat-dialog-custom/mat-dialog-custom.component';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  interval,
  EMPTY,
  of,
  concatMap,
  map,
} from 'rxjs';
import { FormatCurrencyService } from '@services/format-currency.service';
import { withdrawLimitValidator } from '../withdraw-limit.validator';
import { TreoMessage } from '@treo/components/message';
import { DevCycleService } from '@services/dev-cycle.service';
import { TranslateService } from '@ngx-translate/core';
import { BankAccountRecord } from '@interfaces/bank-accounts.interface';
import { SubSink } from 'subsink';

@Component({
  selector: 'ew-withdraw-money-v1',
  templateUrl: './withdraw-money-v1.component.html',
  styleUrls: ['./withdraw-money-v1.component.scss'],
})
export class WithdrawMoneyV1Component implements OnInit, OnDestroy {
  subs: SubSink = new SubSink();
  @ViewChild(MatStepperCustomComponent) child!: MatStepperCustomComponent;
  send: boolean = false;
  currencies: any[] = [];
  loadingCurrencies: boolean = false;
  currency: any = new FormControl('', [Validators.required]);
  loadingRate: boolean = false;
  bankAccount: any = new FormControl('', [Validators.required]);
  rate: {
    amountFrom: number;
    amountTo: number;
    dstCurrency: string;
    srcCurrency: string;
    fee: number;
    fx?: any;
  } = {
    amountFrom: 0,
    amountTo: 0,
    dstCurrency: '',
    srcCurrency: '',
    fee: 0,
  };
  validatedCode: boolean = false;
  loadingCreateWithdrawMoney: boolean = true;
  lockSendPayoutSubmit: boolean = false;
  payoutImprovmentsFlag: boolean = false;
  amount: any = new FormControl('', [Validators.required]);
  reference: any = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(20),
    Validators.pattern(/^[a-zA-Z0-9 ]+$/),
  ]);
  paymentMethod = new FormControl(20, []);
  payoutUpdatedValues: {
    balanceBefore: string;
    balanceAfter: string;
    beneficiaryAmount: string;
    fxRateBaseCurrency: string;
    fxRateDestCurrency: string;
  } = {
    balanceBefore: '',
    balanceAfter: '',
    beneficiaryAmount: '',
    fxRateBaseCurrency: '',
    fxRateDestCurrency: '',
  };
  showFirstStep: boolean = true;
  confirmationMessage: string = 'createWithdrawMoney:confirmWithdrawal';
  type: 'PAYOUT' | 'WITHDRAWAL' | undefined;
  message: TreoMessage | null = null;
  applicableFees: {
    active: boolean;
    fee: { value: number; total: number };
  } = {
    active: false,
    fee: {
      value: 0,
      total: 0,
    },
  };

  isDuringUnavailableTimeRangeWindow = 'available';
  unavailableWindowAlert: TreoMessage = {
    appearance: 'soft',
    type: 'error',
    showButtonClose: false,
    showIcon: true,
    title: 'WARNING',
    content: 'createWithdrawMoney:unavailableWeekendWindow',
  };

  ew1889: {
    variable: boolean;
  } = { variable: false };
  PAGINATION_LIMIT: number = 100;
  notificationsToProvidersFeatureFlag!: boolean; // This will be false for V1
  notification: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.email]),
  });

  get withdrawLimitValidator() {
    return withdrawLimitValidator;
  }

  constructor(
    private API: ApiService,
    private HTTP: HttpClient,
    private DIALOG: MatDialogRef<MatDialogCustomComponent>,
    private CYCLE: DevCycleService,
    private formatCurrency: FormatCurrencyService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    private data?: {
      currencyId: string;
      transactionId: string;
      providerBankAccount: BankAccountRecord;
      type: 'PAYOUT' | 'WITHDRAWAL' | undefined;
    },
  ) {
    if (this.data?.providerBankAccount) {
      this.showFirstStep = false;
      this.bankAccount.setValue(this.data.providerBankAccount);
      this.confirmationMessage = 'createWithdrawMoney:confirmPayment';
    }

    this.type = this.data?.type;

    if (this.type === 'PAYOUT') {
      this.confirmationMessage = 'createWithdrawMoney:confirmPayment';
    }
  }
  async ngOnInit(): Promise<void> {
    this.applicableFees.active = await this.CYCLE.getVariable(
      'ew-1585-applicable-fees',
      false,
    );
    this.ew1889.variable = await this.CYCLE.getVariable(
      'ew-1889-different-field-for-us-and-ca',
      false,
    );
    this.notificationsToProvidersFeatureFlag = false; // Explicitly set to false for V1

    this.subs.add(
      this.currency.valueChanges.pipe(this.isFxAvailable).subscribe(),
      this.bankAccount.valueChanges.pipe(this.isFxAvailable).subscribe(),
    );

    this.getCurrencyAccounts();
    const inputDebounceTime = 500; // 0.5s
    const pollingInterval = 60_000; // 60s

    // Add validations for the amount.
    this.currency.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() =>
          this.amount.value ? this.callGetRate(this.amount.value) : EMPTY,
        ),
      )
      .subscribe({
        next: (data: any) => {
          this.handleApiResponse(data);
          this.amount.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.max(this.currency.value.balance),
          ]);
        },
      });

    // Creating debounce to the API request
    this.amount.valueChanges
      .pipe(
        debounceTime(inputDebounceTime),
        distinctUntilChanged(),
        filter((value) => this.validateAmountInput(value)),
        switchMap((value) => this.callGetRate(value)),
      )
      .subscribe({ next: (data: any) => this.handleApiResponse(data) });

    // Creating API polling
    this.subs.add(
      interval(pollingInterval)
        .pipe(
          filter(() => this.validateAmountPolling(this.amount.value)),
          switchMap(() => this.callGetRate(this.amount.value)),
        )
        .subscribe({
          next: (data: any) => this.handleApiResponse(data),
        }),
    );
  }

  private isFxAvailable = concatMap((changed: any) => {
    const bankOrCurrency = changed.currencyIso ? 'currency' : 'bank';

    const currency = bankOrCurrency === 'bank' ? this.currency.value : changed;
    const bank =
      bankOrCurrency === 'currency' ? this.bankAccount.value : changed;

    const LAST_WEEK_DAY = 6;
    const START_TIME = 2;
    const FIRST_WEEK_DAY = 0;
    const END_TIME = 20;

    const date = new Date();
    const today = date.getUTCDay();
    const hour = date.getUTCHours();

    if (currency.currencyIso == bank.currency) {
      this.isDuringUnavailableTimeRangeWindow = 'available';
      return of(this.isDuringUnavailableTimeRangeWindow);
    }

    return of([today, hour]).pipe(
      map(([weekDay, hourOfTheDay]) => {
        const isUnavailable =
          (weekDay >= LAST_WEEK_DAY && hourOfTheDay >= START_TIME) ||
          (weekDay === FIRST_WEEK_DAY && hourOfTheDay < END_TIME);

        this.isDuringUnavailableTimeRangeWindow = isUnavailable
          ? 'unavailable'
          : 'available';
        return this.isDuringUnavailableTimeRangeWindow;
      }),
    );
  });

  get validations() {
    const amountIsLessThanBalance =
      this.rate?.amountFrom + this.applicableFees.fee.value <=
      this.currency.value.balance;
    const bankAccountWasSelected = this.bankAccount.value.currency;
    const rateIsLoading = this.loadingRate == false;
    const isInUnavailableWindow =
      this.isDuringUnavailableTimeRangeWindow === 'unavailable';
    const rateRequestFinished = this.rate?.amountFrom;
    const amountValid = this.amount.valid;

    const validationsWithoutFirstStep = [
      this.currency.valid,
      rateRequestFinished &&
        amountIsLessThanBalance &&
        rateIsLoading &&
        bankAccountWasSelected &&
        this.bankAccount.valid &&
        !this.lockSendPayoutSubmit &&
        this.reference.valid &&
        amountValid &&
        !isInUnavailableWindow,
    ];

    if (this.showFirstStep) {
      validationsWithoutFirstStep.unshift(bankAccountWasSelected);
    }

    return validationsWithoutFirstStep;
  }

  get getPayload() {
    return {
      bankAccountId: this.bankAccount.value.bankAccountId,
      currencyAccountId: this.currency.value.companyCurrencyAccountId,
      amount: this.rate.amountFrom,
      reference: this.reference.value,
      ...(this.ew1889.variable && {
        beneficiaryDataPaymentType: this.paymentMethod.value,
      }),
    };
  }

  getCurrencyAccounts(): void {
    this.loadingCurrencies = true;

    this.API.get('v1/company-currency-accounts/').subscribe((data: any) => {
      this.currencies = data.records;
      if (this.data?.currencyId) {
        this.currency.setValue(
          this.currencies.find(
            (e) => e.companyCurrencyAccountId == this.data?.currencyId,
          ),
        );
        this.child.myStepper.next();
      }
      this.loadingCurrencies = false;
    });
  }

  createWithdrawMoney(code: string): void {
    var headers = new HttpHeaders();
    headers = headers.append('x-2-factor-auth', code);
    this.loadingCreateWithdrawMoney = true;
    this.lockSendPayoutSubmit = true;
    this.message = null;
    this.API.post(
      `/v1/payouts/`,
      {
        ...this.getPayload,
        type: this.data?.type,
      },
      {
        headers: headers,
      },
    ).subscribe(
      (data: any) => {
        this.validatedCode = true;
        this.loadingCreateWithdrawMoney = false;
        this.DIALOG.close();
        this.API.alertService.success('createWithdrawMoney:messageSuccess');
        this.lockSendPayoutSubmit = false;
      },
      (err) => {
        this.loadingCreateWithdrawMoney = false;
        this.lockSendPayoutSubmit = false;
        this.message = {
          appearance: 'outline',
          type: 'warn',
          showButtonClose: false,
          showIcon: true,
          customIcon: '',
          title: 'alert:errorTitle',
          content: this.translate.instant(
            `createWithdrawMoney:${err.error.message}`,
          ),
        };
      },
    );
  }

  validateAmountInput(value: any): boolean {
    this.message = null;
    value = parseFloat(value);
    if (isNaN(value)) {
      return false;
    }

    if (value < 1 || value > this.currency.value.balance) {
      this.lockSendPayoutSubmit = true;
      this.getUpdatedValues();
      return false;
    }

    this.lockSendPayoutSubmit = false;
    return true;
  }

  validateAmountPolling(value: any): boolean {
    value = parseFloat(value);

    if (isNaN(value) || value < 1 || value > this.currency.value.balance) {
      return false;
    }

    return true;
  }

  getUpdatedValues(): void {
    let balanceBefore = '';
    let balanceAfter = '';
    let beneficiaryAmount = '';
    let fxRateBaseCurrency = '';
    let fxRateDestCurrency = '';

    // TODO: Do something here to add applicable fees.

    if (this.currency) {
      balanceBefore = this.formatCurrency.formatAmount(
        this.currency.value.balance,
        this.currency.value.currencyIso,
      );

      balanceAfter =
        this.getCurrencySymbol(this.currency.value.currencyIso) + ' - ';

      fxRateBaseCurrency = this.formatCurrency.formatAmount(
        1,
        this.currency.value.currencyIso,
      );
    }

    if (this.bankAccount) {
      beneficiaryAmount =
        this.getCurrencySymbol(this.bankAccount.value.currency) +
        ' - ' +
        this.bankAccount.value.currency;
      fxRateDestCurrency =
        this.getCurrencySymbol(this.bankAccount.value.currency) +
        ' - ' +
        this.bankAccount.value.currency;
    }

    if (this.amount.value > 1) {
      balanceAfter = this.formatCurrency.formatAmount(
        this.currency.value.balance -
          (this.amount.value + this.applicableFees.fee.value),
        this.currency.value.currencyIso,
      );
    }

    if (this.rate.amountTo) {
      beneficiaryAmount = this.formatCurrency.formatAmount(
        this.rate.amountTo,
        this.rate.dstCurrency,
      );

      fxRateDestCurrency = this.formatCurrency.formatAmount(
        this.rate.fx,
        this.rate.dstCurrency,
      );
    }

    this.payoutUpdatedValues = {
      balanceBefore,
      balanceAfter,
      beneficiaryAmount,
      fxRateBaseCurrency,
      fxRateDestCurrency,
    };
  }

  callGetRate(value: any) {
    if (typeof value === 'string') {
      value = parseFloat(value);
    }

    this.loadingRate = true;
    this.rate = {
      ...this.rate,
      amountFrom: value,
      dstCurrency: '',
      amountTo: 0,
    };
    return this.API.post(`v1/payouts/get-rate/`, this.getPayload);
  }

  get applicableFee() {
    return this.formatCurrency.formatAmount(
      this.applicableFees.fee.value,
      this.rate.srcCurrency,
    );
  }

  get applicableTotalAmount() {
    return this.formatCurrency.formatAmount(
      this.applicableFees.fee.total,
      this.rate.srcCurrency,
    );
  }

  get actualBalance() {
    return (
      this.currency.value.balance -
      (this.amount.value + this.applicableFees.fee.value)
    );
  }

  getCurrencySymbol(currency: string): string {
    return this.formatCurrency.getCurrencySymbol(currency);
  }

  handleApiResponse({
    records,
  }: {
    records: {
      amountFrom: number;
      amountTo: number;
      dstCurrency: string;
      srcCurrency: string;
      fee: number;
      fx: string;
    };
    success: boolean;
  }) {
    this.rate = records;
    const fxRate = this.rate.amountTo / this.rate.amountFrom;
    this.applicableFees.fee.total = this.rate.amountFrom + this.rate.fee;
    this.applicableFees.fee.value = this.rate.fee;
    this.rate.fx = fxRate.toFixed(2);
    this.loadingRate = false;
    this.getUpdatedValues();
  }

  addAmountValidator() {
    this.amount.addAsyncValidators(
      withdrawLimitValidator(
        this.HTTP,
        this.API,
        this.currency.value.currencyIso,
        this.bankAccount.value.currency,
      ),
    );
    this.amount.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
